import { apiFetch } from './client';

export interface SessionSummary {
  session_id: string;
  class_id: string;
  instance_id: string;
  agent_id: string;
  created_ms: number;
  last_active_ms: number;
  turn_count: number;
  active_turn_count: number;
  channel_id: string | null;
}

export interface ToolCallPreview {
  id?: string;
  name?: string;
  arguments?: Record<string, unknown>;
}

export interface SessionTurn {
  turn_id: number;
  role: string;
  timestamp_ms: number;
  content: string;
  tool_calls: ToolCallPreview[] | null;
  tool_call_id: string | null;
  token_count: number | null;
  compacted: number | boolean;
}

/**
 * ``GET /api/sessions/usage`` — **one row per running instance**, each
 * carrying `class_id` and `instance_id`.
 *
 * It is a list, not an object, and this file used to say otherwise: it
 * declared a `UsageSummary` with `total_input_tokens` and a `by_day`
 * array that the endpoint has never returned. `SessionsPage` read those
 * fields off the array and its header rendered
 * `— in · — out · undefined turns total` — `formatTokens(undefined)`
 * answers an em dash, so two thirds of the defect looked like "no data
 * yet" and only `total_turns` gave it away.
 *
 * A row whose store could not be read carries **`error` instead of the
 * counters** (`machinai/web/api/sessions.py:43-51`), so every counter is
 * optional and a reader has to skip such a row rather than sum through
 * it. `undefined + number` is `NaN`, and a `NaN` total renders as a
 * plausible dash rather than as the failure it is.
 */
export interface InstanceUsage {
  class_id?: string;
  instance_id?: string;
  error?: string;
  total_input_tokens?: number;
  total_output_tokens?: number;
  total_sessions?: number;
  total_turns?: number;
  total_cache_hit_tokens?: number;
  total_cache_miss_tokens?: number;
  total_reasoning_tokens?: number;
  total_cost_usd?: number | null;
}

/** Sum one counter across instances, skipping rows that carry an error. */
export function sumUsage(
  rows: InstanceUsage[],
  field: keyof InstanceUsage,
): number {
  return rows.reduce((total, row) => {
    if (row.error) return total;
    const value = row[field];
    return typeof value === 'number' ? total + value : total;
  }, 0);
}

export function fetchSessions(): Promise<SessionSummary[]> {
  return apiFetch<SessionSummary[]>('/api/sessions');
}

export function fetchSessionHistory(sessionId: string): Promise<SessionTurn[]> {
  return apiFetch<SessionTurn[]>(`/api/sessions/${sessionId}/history`);
}

export function fetchUsage(): Promise<InstanceUsage[]> {
  return apiFetch<InstanceUsage[]>('/api/sessions/usage');
}
